import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { buildDocumentTitle, resolveTitleKey } from "src/routeTitles"

/**
 * Writes the per-route browser-tab title. Mounted once, in `App`.
 *
 * Nothing is written while the app is iframed (LOCKER-409 DoD12). An iframe's
 * `document.title` never reaches the browser tab, so the write would be inert,
 * and an inert write hides the intent from whoever reads this next.
 *
 * The discriminator is the RUNTIME frame check `window.self !== window.top`, and
 * deliberately not the `/os/embedded/*` path prefix: those are ordinary routes
 * that can be opened standalone in a tab, where the title IS wanted. `?mode=RxPro`
 * is a third mode that is not necessarily iframed, and is covered by the same
 * runtime check rather than by a mode of its own.
 *
 * The check is evaluated inside the effect rather than at module load so that it
 * tracks the window the app actually runs in.
 */
export const useDocumentTitle = (): void => {
  const { pathname } = useLocation()
  const { t, i18n } = useTranslation()

  useEffect(() => {
    if (window.self !== window.top) {
      return
    }

    document.title = buildDocumentTitle(t(resolveTitleKey(pathname)))
    // `i18n.language` is a dependency in its own right: `t` is re-created on a
    // language change, but depending on the language makes that explicit.
  }, [pathname, t, i18n.language])
}

export default useDocumentTitle
